import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  format,
  parseISO,
  startOfYear,
  subMonths,
  subYears,
  formatDate,
} from "date-fns";
import {
  ROLE_CHOICES,
  RoleType,
  type TransformedData,
  TaxOptimizePageType,
} from "./types";
import type { Dispatch, SetStateAction } from "react";
import {
  type FirmType,
  type AllTransactionsType,
  type FamilyType,
  type UserType,
} from "../graphql/generated/graphql";
import { FILING_STATUS } from "./constant";
import { TaxProposalType } from "./validations/taxOptimize";
// eslint-disable-next-line boundaries/element-types
import { type GrantCheckoutFormData } from "@/entities/grant/grant-form/utils";
import { PaymentTimeType } from "../constants/grant";
import { CustomGraphQLError } from "./api/graphql/fetchGrapQL";
import { applyTerminology } from "./terminology";

// Handy for combining tailwind classes in components + Gives the ability to add conditional styling.
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export const BREAKPOINT_SM = 640; // Tailwind's sm breakpoint

export const getCurrentYear = (): number => new Date().getFullYear();

export const isServer = (): boolean => typeof window === "undefined";

type PhoneFormat = "dash" | "bracket";

export const formatPhoneNumber = (
  input: string | number | null | undefined,
  format: PhoneFormat = "dash",
): string => {
  if (!input) return "";

  // Convert to string to handle cases where input might be a number or other type
  const inputString = String(input);
  const digits = inputString.replace(/\D/g, "").slice(0, 10); // Sanitize + limit to 10 digits
  const [area, prefix, line] = [
    digits.slice(0, 3),
    digits.slice(3, 6),
    digits.slice(6, 10),
  ];

  if (format === "bracket") {
    if (!digits.length) return "";
    if (digits.length <= 3) return `(${area}`;
    if (digits.length <= 6) return `(${area}) ${prefix}`;
    return `(${area}) ${prefix}-${line}`;
  }

  // Default to "dash"
  if (digits.length <= 3) return area;
  if (digits.length <= 6) return `${area} - ${prefix}`;
  return `${area} - ${prefix} - ${line}`;
};

// Add phone number formatting handler
export const handlePhoneNumberChange = (
  value: string,
  onChange: (value: string) => void,
) => {
  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, "");

  // Limit to 10 digits
  const limitedDigits = digitsOnly.slice(0, 10);

  // Format the phone number
  const formatted = formatPhoneNumber(limitedDigits, "bracket");

  // Update the form value
  onChange(formatted);
};

// Remove phone number formatting
export const removePhoneNumberFormatting = (value: string) => {
  // Remove all non-digit characters
  return value.replace(/\D/g, "");
};

export function formatDateInput(value: string) {
  const digits = value.replace(/\D/g, "");

  if (digits.length <= 1) {
    return digits;
  } else if (digits.length <= 3) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  } else {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  }
}

export function formatZipcodeInput(value: string) {
  return value.replace(/\D/g, "").slice(0, 5);
}

export const getFormattedDate = (
  dateString: string,
  formatString?: string,
): string => {
  if (!dateString) return "-";
  const date = parseISO(dateString);
  return format(date, formatString || "MMM d, yyyy");
};

/**
 * Formats a number to a compact currency string with appropriate suffix (K, M, B, etc.)
 * @param value - The number to format
 * @param decimals - Number of decimal places to show (default: 1)
 * @returns Formatted string (e.g., "$1.2K", "$1.2M", "$1.2B")
 */
export const formatCompactCurrency = (
  value: number,
  decimals: number = 1,
): string => {
  if (value === 0) return "$0";

  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  // 1e -> 10^
  // 1e12 -> 10^12 -> T
  // 1e9 -> 10^9 -> B
  // 1e6 -> 10^6 -> M
  // 1e3 -> 10^3 -> K

  if (absValue >= 1e12) {
    return `${sign}$${(absValue / 1e12).toFixed(decimals)}T`;
  }
  if (absValue >= 1e9) {
    return `${sign}$${(absValue / 1e9).toFixed(decimals)}B`;
  }
  if (absValue >= 1e6) {
    return `${sign}$${(absValue / 1e6).toFixed(decimals)}M`;
  }
  if (absValue >= 1e3) {
    return `${sign}$${(absValue / 1e3).toFixed(decimals)}K`;
  }

  return `${sign}$${absValue.toFixed(decimals)}`;
};

/**
 * Formats a number to a standard currency string with commas
 * @param value - The number to format
 * @param decimals - Number of decimal places to show (default: 0)
 * @param trimTrailingZeros - When true, drop the fraction if it is all zeros
 *   (e.g. `formatCurrency(546, 2, true)` → "$546" instead of "$546.00"),
 *   while keeping real cents (`formatCurrency(545.76, 2, true)` → "$545.76").
 *   Default: false — existing callers continue to get exactly `decimals` places.
 * @returns Formatted string (e.g., "$100,000", "$1,234.56")
 */
export const formatCurrency = (
  value: number,
  decimals: number = 0,
  trimTrailingZeros: boolean = false,
): string => {
  if (value === 0) return "$0";

  const absValue = Math.abs(value);

  // When trimTrailingZeros is true, render up to `decimals` decimal places but
  // drop the fraction entirely if it's all zeros (e.g. "$546.00" → "$546"),
  // while real cents like "$545.76" are kept. When false (default), behavior
  // is unchanged: exactly `decimals` decimal places are always shown.
  const minimumFractionDigits = trimTrailingZeros ? 0 : decimals;

  return `$${absValue.toLocaleString("en-US", {
    minimumFractionDigits,
    maximumFractionDigits: decimals,
  })}`;
};

export const parseCurrencyString = (value: string | number): number => {
  if (typeof value === "number") return value;
  if (!value) return 0;
  // Remove any currency symbols, commas, and whitespace
  const cleanValue = value.replace(/[$,]/g, "").trim();
  // Convert to number
  const number = parseFloat(cleanValue);
  // Return 0 if the result is NaN
  return isNaN(number) ? 0 : number;
};

export const formatCurrencyWithoutDollarSymbol = (
  value: number,
  decimals: number = 0,
): string => {
  if (value === 0) return "0";

  const sign = value < 0 ? "-" : "";
  const absValue = Math.abs(value);

  return `${sign}${absValue.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
};

// Capitalizes the first character of a string
export function capitalizeFirst(str: string): string {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Letter initials from a full name, e.g. "Stephen Jack" -> "SJ" (max 2)
export function getInitials(name?: string | null): string {
  return (
    (name ?? "")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") ?? ""
  );
}

export function formatCurrencyInputWithoutDollarSymbol(value: number): string {
  if (!value) return "";
  return value.toLocaleString("en-US");
}

export function setLocalStorage(key: string, value: string): void {
  if (typeof window !== "undefined") {
    localStorage.setItem(key, value);
  }
}

export function removeLocalStorage(key: string): void {
  if (typeof window !== "undefined") {
    localStorage.removeItem(key);
  }
}

export function getLocalStorage(key: string): string {
  if (typeof window !== "undefined") {
    return localStorage.getItem(key) || "";
  }
  return "";
}

/**
 * A campaign start/end date as the instant the API expects: the picked day at
 * UTC midnight. The picker hands back a local-midnight Date, so the calendar
 * day is read off it and re-stamped in UTC — otherwise a user behind UTC
 * sends the previous day.
 */
export function getCampaignDateAtMidnight(
  date?: Date | null,
): Date | undefined {
  if (!date) return undefined;
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0),
  );
}

/**
 * Sanitizes a price string by removing all special characters and converting to a number.
 * @param price - The price string to sanitize (e.g. "13,000.00" or "$13,000")
 * @returns The sanitized number (e.g. 13000)
 */
export function sanitizePrice(
  price: string | number | undefined | null,
): number {
  if (price === undefined || price === null) return 0;
  if (typeof price === "number") return price;
  return Number(price.replace(/[^0-9.]/g, "")) || 0;
}

export const fixValueToTwoDecimals = (value: number): number => {
  return Number(value.toFixed(2));
};

export function getFamilyId(): string {
  if (typeof window !== "undefined") {
    return localStorage.getItem("familyId") || "";
  }
  return "";
}

export function getFirmId(): string {
  if (typeof window !== "undefined") {
    return localStorage.getItem("firmId") || "";
  }
  return "";
}

export const monthOrder = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const transformData = (rawData: any[]) => {
  const monthlyData: Record<string, TransformedData> = {};
  const allDates: Set<string> = new Set();

  rawData.forEach((item) => {
    let rawDate = item?.date;

    // Truncate microseconds (6 digits) to milliseconds (3 digits)
    rawDate = rawDate?.replace(/\.(\d{3})\d{3}([+-]\d{2}:\d{2})/, ".$1$2");

    const dateObj = new Date(rawDate);

    if (isNaN(dateObj.getTime())) {
      console.warn("Invalid date:", item?.date);
      return;
    }

    const isoDate = dateObj.toISOString().split("T")[0];
    const month = dateObj.toLocaleString("default", { month: "short" });
    const amount = parseFloat(item?.amount?.replace(/[^0-9.-]+/g, "") || "0");
    const cleanAmount = Math.abs(amount);
    const type = item?.type;

    allDates.add(isoDate);

    if (!monthlyData[month]) {
      monthlyData[month] = {
        date: isoDate,
        month,
        Grants: 0,
        Contributions: 0,
        Campaigns: 0,
        meta: {},
      };
    }

    if (type === "Grant") {
      monthlyData[month].Grants += cleanAmount;
      monthlyData[month].meta.Grants = {
        date: item?.date,
        description: item?.description,
        amount,
        id: item?.id,
        status: item?.status,
      };
    }
    if (type === "Contribution") {
      monthlyData[month].Contributions += cleanAmount;
      monthlyData[month].meta.Contributions = {
        date: item?.date,
        description: item?.description,
        amount,
        id: item?.id,
        status: item?.status,
      };
    }
    if (type === "Campaign") {
      monthlyData[month].Campaigns += cleanAmount;
      monthlyData[month].meta.Campaigns = {
        date: item?.date,
        description: item?.description,
        amount,
        id: item?.id,
        status: item?.status,
      };
    }
  });

  return Object.values(monthlyData);
};

export const url = (_url: string): string => {
  if (!_url || _url.startsWith("http")) return _url;
  return "http://" + _url;
};

export function formatTransactionDescription(
  txn: AllTransactionsType | undefined,
): string {
  if (!txn?.description) return "-";
  return `${txn?.description} - ${txn?.contributor}`;
}

export const getStatusColor = (status?: string) => {
  if (!status) return "text-gray-600";
  const normalizedStatus = status.toLowerCase();
  switch (normalizedStatus) {
    case "complete":
      return "text-green-600";
    case "scheduled":
      return "text-[#3C3C3C] border border-[#3C3C3C]";
    case "processing":
      return "text-blue-600";
    case "pending":
      return "text-blue-500";
    case "reconciled":
      return "text-green-500";
    case "cancelled":
      return "text-[#A8A8A8]";
    default:
      return "text-gray-600";
  }
};

export const capitalizeFirstLetter = (text: string) => {
  if (!text) return "";
  return text?.charAt(0).toUpperCase() + text?.slice(1)?.toLowerCase();
};

export const defaultStartDate = "01/01/25";
export const defaultEndDate = "05/06/25";

export const dollarToCents = (amount: number): number => {
  // Use Number.toFixed(2) to round the number to two decimal places,
  // then remove the decimal point, and pad the string with zeros if needed.
  return Number(amount.toFixed(2).replace(".", "").padEnd(3, "0"));
};

export const sortByDate = (
  data: any[],
  dateKey: string,
  ascending: boolean = true,
) => {
  return data?.sort((a, b) => {
    const dateA = new Date(a[dateKey]);
    const dateB = new Date(b[dateKey]);
    return ascending
      ? dateA?.getTime() - dateB?.getTime()
      : dateB?.getTime() - dateA?.getTime();
  });
};

export const formatPrice = (price: string) => {
  return +price;
};

export const loadMoreData = (
  newPage: number,
  setPage: Dispatch<SetStateAction<number>>,
) => {
  setPage(newPage);
};

export const randomKey = () => {
  const maxNumber = 999999999;
  const randomNumber = Math.floor(Math.random() * maxNumber + 1);
  return randomNumber;
};

export const shouldAllowMulticoreInvestments: boolean = false;
// TODO: remove this function after S3 bucket is updated
export const transformImageUrl = (url: string) => {
  if (typeof url !== "string" || url.startsWith("blob:")) {
    return url;
  }

  // Only transform URLs on the client side to avoid SSR mismatches
  if (typeof window === "undefined") {
    return url;
  }

  return url;
};

export const handleTimePeriodClick = (
  period: string,
  setStartDate: Dispatch<SetStateAction<Date | null>>,
  setEndDate: Dispatch<SetStateAction<Date | null>>,
  setSelectedPeriod: Dispatch<SetStateAction<string>>,
) => {
  const today = new Date();
  let newStartDate = today;
  let newEndDate = today;

  switch (period) {
    case "3M":
      newStartDate = subMonths(today, 3);
      break;
    case "6M":
      newStartDate = subMonths(today, 6);
      break;
    case "1Y":
      newStartDate = subYears(today, 1);
      break;
    case "3Y":
      newStartDate = subYears(today, 3);
      break;
    case "YTD":
      newStartDate = startOfYear(today);
      break;
    case "2024":
      newStartDate = new Date(2024, 0, 1);
      newEndDate = new Date(2024, 11, 31);
      break;
  }

  setStartDate(newStartDate);
  setEndDate(newEndDate);
  setSelectedPeriod(period);
};

/**
 * Downloads a PDF file from a base64-encoded string.
 * Handles optional b'...' wrapping and whitespace/newline cleanup.
 * @param base64Data - The base64-encoded PDF string
 * @param filename - The filename for the downloaded PDF
 */
export function downloadBase64Pdf(base64Data: string, filename: string) {
  if (!base64Data) return;
  let cleanBase64 = base64Data;
  if (cleanBase64.startsWith("b'") && cleanBase64.endsWith("'")) {
    cleanBase64 = cleanBase64.slice(2, -1);
  }
  cleanBase64 = cleanBase64
    .replace(/\\n/g, "")
    .replace(/\r?\n|\r/g, "")
    .replace(/\s/g, "");
  const link = document.createElement("a");
  link.href = `data:application/pdf;base64,${cleanBase64}`;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Downloads a file from a base64-encoded string.
 * @param base64Data - The base64-encoded file string
 * @param filename - The filename for the downloaded file
 */
export function downloadBase64File(base64Data: string, filename: string) {
  if (!base64Data) return;
  const link = document.createElement("a");
  link.href = `data:application/pdf;base64,${base64Data}`;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Cleans a base64 string by removing b'...' wrapping and trailing single quotes.
 * @param data - The base64-encoded string
 * @returns The cleaned base64 string
 */
export function cleanBase64(data: string) {
  if (!data) return "";
  if (data.startsWith("b'")) data = data.slice(2);
  if (data.endsWith("'")) data = data.slice(0, -1);
  return data;
}

/**
 * Checks if the total allocation would exceed 100%.
 * @param beneficiaries - Array of beneficiaries with id and percentage
 * @param successors - Array of successors with id and percentage
 * @param newAllocation - The new allocation value to check
 * @param excludeId - Optional id to exclude (for edit scenarios)
 * @returns true if total allocation would exceed 100%, false otherwise
 */
export function wouldExceedTotalAllocation({
  beneficiaries,
  successors,
  newAllocation,
  excludeId,
}: {
  beneficiaries: { id?: any; percentage?: number }[];
  successors: {
    id?: any;
    percentage?: number;
    isContingent?: boolean | null;
  }[];
  newAllocation: number;
  excludeId?: any;
}): boolean {
  const totalAllocated =
    [
      ...beneficiaries.filter((b) => b.id !== excludeId),
      ...successors.filter((s) => s.id !== excludeId && !s.isContingent),
    ].reduce((sum, item) => sum + (item.percentage || 0), 0) +
    Number(newAllocation);
  return totalAllocated > 100;
}

/** Succession allocations hold up to 2 decimals (e.g. 20.58%). */
export const MIN_ALLOCATION_PERCENT = 0.01;
export const ALLOCATION_PERCENT_PATTERN = /^\d+(\.\d{1,2})?$/;

/** Rounds to hundredths so float drift (0.1 + 0.2) never leaks into a total. */
export const roundPercent = (value: number): number =>
  Math.round(value * 100) / 100;

/** Sums in whole hundredths, so 33.33 + 33.33 + 33.34 is exactly 100. */
export const sumPercentages = (values: (number | null | undefined)[]): number =>
  values.reduce<number>(
    (total, value) => total + Math.round((value ?? 0) * 100),
    0,
  ) / 100;

/** Display form without trailing zeros: 40 → "40", 20.5 → "20.5", 20.58 → "20.58". */
export const formatPercent = (value?: number | null): string =>
  String(roundPercent(Number(value) || 0));

export const sortByString = (
  data: any[],
  stringKey: string,
  ascending: boolean = true,
) => {
  return [...data].sort((a, b) => {
    const aVal = a?.[stringKey]?.toLowerCase?.() ?? "";
    const bVal = b?.[stringKey]?.toLowerCase?.() ?? "";

    return ascending ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });
};

export const renderActivityTime = (value: string) => {
  const date = new Date(value);
  // if in last 2 mins - show "just now"
  if (date.getTime() > Date.now() - 2 * 60 * 1000) {
    return "just now";
  }
  // if in last hour - show "x mins ago"
  if (date.getTime() > Date.now() - 1 * 60 * 60 * 1000) {
    return `${Math.floor((Date.now() - date.getTime()) / (60 * 1000))} mins ago`;
  }
  // if today - show "x hours ago"
  if (date.getTime() > Date.now() - 1 * 24 * 60 * 60 * 1000) {
    return `${Math.floor((Date.now() - date.getTime()) / (60 * 60 * 1000))} hours ago`;
  }

  return formatDate(date, "MMM d");
};

export const getCurrentPath = (): string => {
  if (typeof window !== "undefined") {
    return sessionStorage.getItem("currentPath") || "";
  }
  return "";
};

export const setCurrentPath = (path: string): void => {
  if (typeof window !== "undefined") {
    sessionStorage.setItem("currentPath", path);
  }
};

export const setPreviousPath = (path: string): void => {
  if (typeof window !== "undefined") {
    sessionStorage.setItem("prevPath", path);
  }
};

export const getPreviousPath = (): string => {
  if (typeof window !== "undefined") {
    return sessionStorage.getItem("prevPath") || "";
  }
  return "";
};

export const removeCurrentPath = (): void => {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem("currentPath");
  }
};

export const removePreviousPath = (): void => {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem("prevPath");
  }
};

/**
 * Detect if the user is navigating to a new route within the same role.
 * For example, if the previous route is /client/123/overview and the current route is /client/123/overview,
 * then it is a internal route change. Else, it is an external route change.
 *
 * This is used to determine if the user is impersonating a client and needs to exit the impersonation mode.
 * If this is not an internal route change, then we need to exit the impersonation mode. Else, we can stay in
 * the impersonation mode.
 */
export const isExternalRouteChange = (
  currentPath: string,
  previousPath: string,
): boolean => {
  // Get the first segment of the current and previous paths.
  const currentPathFirstSegment = currentPath.split("/")[1];
  const previousPathFirstSegment = previousPath.split("/")[1];

  // We need both current and previous path to be defined to determine if it is a external route change.
  if (!currentPathFirstSegment || !previousPathFirstSegment) {
    return false;
  }

  // If the first segment is the same, then it is an internal route change.
  if (currentPathFirstSegment === previousPathFirstSegment) {
    return false;
  }

  // If the segments are different, then it is an external route change.
  return true;
};

export const QUERY_PAGE_SIZE = 10;

export const pageArray = (page: number) => {
  const result = [];
  const limit = page * QUERY_PAGE_SIZE;
  for (let i = QUERY_PAGE_SIZE; i <= limit; i += QUERY_PAGE_SIZE) {
    result.push(i);
  }
  return result;
};

export function capitalizeFirstLetterOfEachWord(input: string): string {
  return input
    .split(" ")
    .map(
      (word) => word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase(),
    )
    .join(" ");
}

export const formatMMDDYYYY = (dateInput: string | Date): string => {
  const date = new Date(dateInput);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

export const EMAIL_REGEX =
  /^([A-Za-z0-9._%+-])+@([A-Za-z0-9._-])+\.([A-Za-z]{2,4}[A-Za-z0-9]*)$/;

export const isValidEmail = (email: string): boolean => {
  return EMAIL_REGEX.test(email);
};

export const toISOStringWithLocalDayPreserved = (
  date?: Date,
): string | undefined =>
  date
    ? new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        12,
        0,
        0,
        0,
      ).toISOString()
    : undefined;

export const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const handleRefetch = async (
  refetchFunction?: () => void,
  setLoading?: (loading: boolean) => void,
) => {
  if (refetchFunction) {
    if (setLoading) {
      setLoading(true);
    }
    await delay(1000);
    await Promise.resolve(refetchFunction());
    if (setLoading) {
      setLoading(false);
    }
  }
};

export function cleanAmount(value: string) {
  const num = parseFloat(value);
  if (Number.isInteger(num)) {
    return num.toString();
  }
  return value;
}

export type StatusPillVariant =
  | "info"
  | "success"
  | "error"
  | "warning"
  | "neutral"
  | "disabled";

export const VARIANT_STYLES: Record<StatusPillVariant, string> = {
  info: "border-brand-primary text-brand-primary bg-white",
  success: "border-green-800 text-green-800 bg-white",
  error: "border-red-600 text-red-600 bg-white",
  warning: "border-yellow-700 text-yellow-700 bg-white",
  neutral: "border-gray-400 text-gray-600 bg-white",
  disabled: "border-disabled text-disabled bg-disabled",
};

export const elevateToGiveStatusMap = {
  Grant: {
    pending: "Pending",
    approve_pending_rebal: "Approved",
    approved_pending_redemption: "Approved",
    approved: "Approved",
    funding_in_process: "Processing",
    funding_complete: "Processing",
    processing: "Processing",
    payment_pending: "Payment Sent",
    cleared: "Completed",
    scheduled: "Scheduled",
    canceled: "Canceled",
    rejected: "Rejected",
    cancelled: "Canceled",
    check_reissued: "Reissued",
    check_expired: "Payment Expired",
    payment_voided: "Voided",
    funding_reserved: "Processing",
    completed: "Completed",
  },
  Contribution: {
    SECURITIES: {
      pending: "Pending",
      pending_review: "Pending",
      posted: "Scheduled",
      cleared: "Processing",
      reconciled: "Processing",
      "received_&_processing": "Processing",
      "liquidated_&_settling": "Liquidated",
      failed: "Failed",
      cancelled: "Canceled",
      canceled: "Canceled",
      scheduled: "Scheduled",
      liquidate_in_process: "Liquidating",
      liquidated: "Liquidated",
      completed: "Completed",
      liquidity_canceled: "Canceled",
      reconciled_canceled: "Canceled",
      rejected: "Rejected",
      added_to_balance: "Completed",
    },
    OTHER: {
      pending: "Pending",
      submitted: "Pending",
      pending_review: "Pending",
      processing: "Processing",
      cleared: "Processing",
      reconciled: "Completed",
      failed: "Failed",
      process_failed: "Failed",
      cancelled: "Canceled",
      canceled: "Canceled",
      cancel_exception: "Canceled",
      rejected: "Rejected",
      scheduled: "Scheduled",
      processing_error: "Failed",
      completed: "Completed",
      added_to_balance: "Completed",
      processing_ach: "Processing",
    },
  },
} as const;

export type ElevateToGiveStatusMap = typeof elevateToGiveStatusMap;

export function getMappedGiveStatus(
  status: string | null | undefined,
  type: "Grant" | "Contribution" | "Campaign",
  source?: string,
): string {
  const key = (status || "").replace(/\s+/g, "_").toLowerCase();
  const normalizedSource = (source || "").replace(/\s+/g, "_").toLowerCase();
  if (type === "Grant") {
    return (
      elevateToGiveStatusMap.Grant[
        key as keyof typeof elevateToGiveStatusMap.Grant
      ] ?? "Unknown"
    );
  }

  if (type === "Contribution") {
    const sourceCategory =
      normalizedSource === "securities" ||
      normalizedSource === "third_party_securities"
        ? "SECURITIES"
        : "OTHER";

    const map = elevateToGiveStatusMap.Contribution[sourceCategory];

    return map[key as keyof typeof map] ?? "Unknown";
  }

  return "Unknown";
}

// Dynamic size classes
export const sizeClasses = {
  sm: {
    input: "h-[40px] text-sm pl-10",
    preview: "w-5 h-5 left-2.5",
  },
  md: {
    input: "h-[50px] text-base pl-12",
    preview: "w-6 h-6 left-3",
  },
  lg: {
    input: "h-[65px] text-lg pl-14",
    preview: "w-7 h-7 left-3.5",
  },
};

/**
 * Validates if a string is a valid 6-digit hex color code
 * @param value - The string to validate
 * @returns true if the string is a valid hex color, false otherwise
 */
export function isValidHexColor(value: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(value);
}

export function formatPhoneNumberForDisplay(
  phoneNumber: Array<string> | string | undefined | null,
): string | null {
  if (!phoneNumber) return null;

  if (Array.isArray(phoneNumber)) {
    return phoneNumber.length > 0 ? phoneNumber[0] : null;
  }

  return phoneNumber;
}

export function getRole(role: string): string | null {
  // If role is already in the correct format, return it
  if ((Object.values(ROLE_CHOICES) as string[]).includes(role as string)) {
    return role;
  }

  // Map from display names to ROLE_CHOICES values
  const roleMapping: Record<string, string> = {
    RIA: ROLE_CHOICES[RoleType.RIA],
    "Family Member": ROLE_CHOICES[RoleType.FamilyMember],
    "Financial Advisor": ROLE_CHOICES[RoleType.FinancialAdvisor],
    Reviewer: ROLE_CHOICES[RoleType.Reviewer],
    HeadOfTheFamily: ROLE_CHOICES[RoleType.HeadOfTheFamily],
    "Advisory Ops": ROLE_CHOICES[RoleType.AdvisoryOps],
    "Corporate Admin": ROLE_CHOICES[RoleType.CorporateAdmin],
    "Self Directed": ROLE_CHOICES[RoleType.SelfDirected],
    "Enterprise Admin": ROLE_CHOICES[RoleType.EnterpriseAdmin],
    Reporter: ROLE_CHOICES[RoleType.Reporter],
    "Ops Admin": ROLE_CHOICES[RoleType.OpsAdmin],
  };

  return roleMapping[role] || role || null;
}
export const redirectRouteBasedOnParams = (
  code: string,
  page: string | null,
  type: TaxOptimizePageType,
): string => {
  const pageParam = page ? `?page=${page}` : "";

  if (type === TaxOptimizePageType.PROPOSAL) {
    return `/advisor/optimize/${code}/proposal${pageParam}`;
  } else if (type === TaxOptimizePageType.RESULTS) {
    return `/advisor/optimize/${code}/result${pageParam}`;
  }
  return `/advisor/optimize/${code}${pageParam}`;
};

/**
 * Converts backend enum key to enum value for form compatibility
 *
 * Backend APIs (GenerateClientTaxOptimisationResult, GetClientLevelSettings)
 * return filingStatus as enum keys in CAPITAL_CASE format (e.g., "MARRIED_FILING_JOINTLY").
 *
 * However, form components and mutations expect enum values in lowercase format
 * (e.g., "married_filing_jointly").
 *
 * This function converts:
 * - "MARRIED_FILING_JOINTLY" → "married_filing_jointly"
 * - "SINGLE" → "single"
 * - "HEAD_OF_HOUSEHOLD" → "head_of_household"
 *
 * @param key - The enum key which comes from backend (e.g., "MARRIED_FILING_JOINTLY")
 * @returns The corresponding enum value for form usage
 */

export const getFilingStatusValue = (key?: string): FILING_STATUS => {
  if (!key) return FILING_STATUS.MARRIED_FILING_JOINTLY;

  return (
    FILING_STATUS[key as keyof typeof FILING_STATUS] ??
    FILING_STATUS.MARRIED_FILING_JOINTLY
  );
};

/**
 * Converts backend enum key to enum value for form compatibility
 *
 * Backend APIs (GetClientLevelSettings)
 * return taxProposalScenario as enum keys in lowercase format (e.g., "donate_charity").
 **/
export const getTaxProposalScenarioValue = (key?: string): TaxProposalType => {
  if (!key) return TaxProposalType.DONATE_CHARITY;

  return (
    TaxProposalType[key as keyof typeof TaxProposalType] ??
    TaxProposalType.DONATE_CHARITY
  );
};

/**
 * Determines the user's role based on context and user data
 * @param user - The user object containing roles and userRoles
 * @param familyId - Optional family ID from route parameters
 * @returns The determined role string
 */
export const determineUserRole = (
  user: UserType,
  familyId?: string,
): string => {
  // For client-specific contexts (HOH/Family Member)
  // When familyId exists in params, we're in a client route that means the client is either HOH or a Family Member
  if (familyId && Array.isArray(user?.userRoles) && user.userRoles.length > 0) {
    const familyRole = user.userRoles.find(
      (item: { family_id: string | number; role: string }) =>
        item.family_id.toString() === familyId,
    );
    return familyRole?.role ?? "";
  }

  // For non-family contexts (Advisor, Reviewer, RIA, etc.)
  // When no familyId in params, use general user roles
  if (Array.isArray(user?.roles) && user.roles.length > 0) {
    return user.roles[0] ?? "";
  }

  // Fallback to default role
  return ROLE_CHOICES[RoleType.FinancialAdvisor];
};

// Helper function to check if there are unattested contributions
export const hasUnattestedContributionsData = (familyDAF: FamilyType) => {
  const { unattestedContributions } = familyDAF;
  if (!unattestedContributions) return false;

  const { securityTransactions, achContributions, wireTransactions } =
    unattestedContributions;

  return (
    (securityTransactions?.length ?? 0) > 0 ||
    (achContributions?.length ?? 0) > 0 ||
    (wireTransactions?.length ?? 0) > 0
  );
};

export const getFormattedMetaData = (
  metaData: string,
): {
  family: string;
  user: string;
} => {
  if (!metaData) {
    return {
      family: "",
      user: "",
    };
  }
  return JSON.parse(JSON.parse(metaData));
};

/**
 * Downloads a PDF file from a given href
 * @param href - The href of the PDF file
 * @param filename - The filename of the PDF file
 */

export function downloadPdf(href: string, filename?: string): void {
  // API routes require an Authorization header — use fetch + blob instead of <a> click
  if (href.startsWith("/api/")) {
    const token = localStorage.getItem("give-session-token");
    fetch(href, {
      headers: { Authorization: token ? `JWT ${token}` : "" },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to download: ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = filename ?? href.split("/").pop() ?? "document.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
      })
      .catch((err) => console.error("[downloadPdf]", err));
    return;
  }

  const origin = window.location.origin ?? "https://app.tifingive.com";
  const filePath = href.endsWith(".pdf") ? href : `${href}.pdf`;
  const url = /^https?:\/\//i.test(filePath)
    ? filePath
    : `${origin}${filePath}`;

  const a = document.createElement("a");
  a.href = url;
  a.download = filename ?? url.split("/").pop() ?? "document.pdf";
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Overlays firm/enterprise document overrides onto the static legal-doc
 * defaults (getLegalDocsMap()) — a doc with no override keeps its default
 * href untouched, matching the "fall back to what we have currently" ask.
 */
export function mergeFirmDocuments<
  T extends { id: string; href?: string; downloadable?: boolean },
>(
  base: { left: T[]; right: T[] },
  overrides: Array<{ documentType: string; documentUrl: string | null }>,
): { left: T[]; right: T[] } {
  const overrideByType = new Map(overrides.map((o) => [o.documentType, o]));
  const apply = (docs: T[]): T[] =>
    docs.map((doc) => {
      const override = overrideByType.get(doc.id);
      if (!override?.documentUrl) return doc;
      return { ...doc, href: override.documentUrl, downloadable: true };
    });
  return { left: apply(base.left), right: apply(base.right) };
}

/**
 * Downloads an onboarding guideline PDF during registration, before the user has
 * an Okta session token. Authorizes with the invite/registration token instead,
 * which the /api/documents/onboarding route re-verifies server-side.
 * Returns a promise that resolves once the file has been handed to the browser,
 * and rejects on failure — so callers can show a loading/error indicator.
 * @param docId - Whitelisted onboarding document id
 * @param filename - Suggested download filename
 * @param registrationToken - The invite token from the registration URL
 */
export async function downloadOnboardingPdf(
  docId: string,
  filename: string,
  registrationToken: string,
): Promise<void> {
  try {
    const res = await fetch(
      `/api/documents/onboarding/${encodeURIComponent(docId)}`,
      { headers: { "x-registration-token": registrationToken } },
    );
    if (!res.ok) throw new Error(`Failed to download: ${res.status}`);

    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch (err) {
    console.error("[downloadOnboardingPdf]", err);
    // Re-throw so callers (e.g. toast.promise) can surface the failure to the user.
    throw err;
  }
}

export function underscoresToSpaces(value?: string) {
  if (!value) return "";
  return value.replace(/_/g, " ");
}

export function grantUpdatedMessage(formData: Partial<GrantCheckoutFormData>) {
  const base = applyTerminology(
    `Successfully updated the ${
      formData.paymentTimeType === PaymentTimeType.RECURRING
        ? `${(formData.frequency ?? "").toLowerCase()} recurring`
        : "one-time"
    } grant to ${formData.charityDetails?.title ?? ""} (EIN: ${
      formData.charityDetails?.ein ?? ""
    }) with new amount of $${formData.giftAmount}`,
  );

  if (formData.paymentTimeType === PaymentTimeType.ONE_TIME) {
    return `${base}, scheduled for ${
      formData.giftDate?.toLocaleDateString() ?? ""
    }`;
  } else {
    return `${base}, starting ${
      formData.startDate?.toLocaleDateString() ?? ""
    } and ending ${formData.endDate?.toLocaleDateString() ?? ""}`;
  }
}

export function grantSubmittedMessage(
  formData: Partial<GrantCheckoutFormData>,
) {
  const base = applyTerminology(
    `Successfully submitted a ${
      formData.paymentTimeType === PaymentTimeType.RECURRING
        ? formData.frequency?.toLowerCase()
        : "one-time"
    } grant of $${formData.giftAmount} to ${
      formData.charityDetails?.title ?? ""
    } (EIN: ${formData.charityDetails?.ein ?? ""})`,
  );

  if (formData.paymentTimeType === PaymentTimeType.ONE_TIME) {
    return `${base}, scheduled for ${
      formData.giftDate?.toLocaleDateString() ?? ""
    }`;
  } else {
    return `${base}, starting ${
      formData.startDate?.toLocaleDateString() ?? ""
    } and ending ${formData.endDate?.toLocaleDateString() ?? ""}`;
  }
}

export function grantCancelledMessage({
  paymentTimeType,
  amount,
  charityName,
  ein,
  frequency,
}: {
  paymentTimeType: PaymentTimeType;
  amount: number | string;
  charityName: string;
  ein: string;
  frequency: string;
}) {
  return applyTerminology(
    `Successfully cancelled the ${
      paymentTimeType === PaymentTimeType.RECURRING
        ? `${(frequency ?? "").toLowerCase()} recurring`
        : "one-time"
    } grant of $${amount} to ${charityName} (EIN: ${ein})`,
  );
}

export function contributionSubmittedMessage(args: {
  type: string;
  amount: number | string;
}) {
  return `Successfully submitted a ${underscoresToSpaces(args.type)} contribution of $${args.amount}`;
}

export const getDateWithCurrentTime = (
  selectedDate?: Date,
): Date | undefined => {
  if (!selectedDate) return undefined;

  // Create a new date with the selected date but current time
  const now = new Date();
  const dateWithTime = new Date(
    selectedDate.getFullYear(),
    selectedDate.getMonth(),
    selectedDate.getDate(),
    now.getHours(),
    now.getMinutes(),
    now.getSeconds(),
  );

  return dateWithTime;
};

export const getMinimumAccountBalance = (
  metaData: FirmType["metaData"],
): number | undefined => {
  let minimumBalance;

  if (metaData) {
    try {
      const parsedMetadata = JSON.parse(metaData);
      const configuredMin = parsedMetadata?.minimumAccountBalance;
      if (typeof configuredMin === "number" && configuredMin > 0) {
        minimumBalance = configuredMin;
        return minimumBalance;
      }
    } catch (error) {
      console.warn("Failed to parse firm metadata:", error);
    }
  }
};

export function getMinimumBalanceWarningMessage(
  metaData?: FirmType["metaData"],
): string {
  const minimumBalance = getMinimumAccountBalance(metaData);
  if (!minimumBalance) return "";
  const formattedAmount = formatCurrencyWithoutDollarSymbol(minimumBalance, 2);
  return applyTerminology(
    `The minimum account balance is $${formattedAmount}. Any grant that brings your account balance to less than $${formattedAmount} is automatically converted to a Full Balance Grant and the closing of your account. Please adjust the grant amount if you wish to keep your account active.`,
  );
}

export function getMinimumBalanceWarningMessageForGrantSummary(
  metaData?: FirmType["metaData"],
): string {
  const minimumBalance = getMinimumAccountBalance(metaData);
  if (!minimumBalance) return "";
  const formattedAmount = formatCurrencyWithoutDollarSymbol(minimumBalance, 2);
  return applyTerminology(
    `By completing this grant, you attest to the closing of your account. If you wish your account remains open, go back and edit your grant amount to keep your account balance above the $${formattedAmount} minimum threshold.`,
  );
}

// Next.js `redirect()` works by throwing a special error with a digest like
// "NEXT_REDIRECT;<type>;<path>;<status>;". Catching it in a try/catch around a
// server component swallows the redirect — re-throw it instead.
export const isNextRedirectError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  if (error.message === "NEXT_REDIRECT") return true;
  const digest = (error as { digest?: unknown }).digest;
  return typeof digest === "string" && digest.startsWith("NEXT_REDIRECT");
};

export const getErrorMessage = (error: Error | unknown): string => {
  if (error instanceof CustomGraphQLError) {
    return (
      error.originalError?.response?.errors?.[0]?.message ||
      error.message ||
      "An error occurred. Please try again."
    );
  }

  if (error instanceof Error) {
    return error.message || "An error occurred. Please try again.";
  }

  return "An error occurred. Please try again.";
};
